import { useState, useEffect, useRef, useCallback } from 'react'
import { ChatMessage, Conversation } from '../types'
import { conversationApi } from '../lib/api/conversations'
import {
  subscribeToConversation,
  unsubscribeFromConversation,
  ConversationUpdateData,
  ConversationCallbacks,
  AiCreativeCreationCompletedData,
  isDisplayableMessage,
  isUserMessage,
  isCampaignAgentMessage,
  formatCampaignAgentMessage,
  shouldShowThinking,
} from '../lib/actionCable'

export interface UseChatOptions {
  advertiserId: number | null
  conversationId: number | null
  onConversationCreated?: (id: number, conversation: Conversation) => void
  onResponseStateUpdate?: (rs: Record<string, unknown>) => void
  /** Raw update callback — receives full ConversationUpdateData so consumers can extract extra fields */
  onRawUpdate?: (data: ConversationUpdateData) => void
  extraSubscriptionCallbacks?: Partial<
    Pick<
      ConversationCallbacks,
      | 'onAdUnitProcessing'
      | 'onAdUnitCreated'
      | 'onMediaBuyComplete'
      | 'onCreativeSyncCompleted'
      | 'onAiCreativeCreationCompleted'
    >
  >
}

export function useChat(options: UseChatOptions) {
  const {
    advertiserId,
    conversationId,
    onConversationCreated,
    onResponseStateUpdate,
    onRawUpdate,
    extraSubscriptionCallbacks,
  } = options

  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([])
  const [isChatLoading, setIsChatLoading] = useState(false)
  const extraCbRef = useRef(extraSubscriptionCallbacks)
  useEffect(() => { extraCbRef.current = extraSubscriptionCallbacks }, [extraSubscriptionCallbacks])

  const onResponseStateUpdateRef = useRef(onResponseStateUpdate)
  useEffect(() => { onResponseStateUpdateRef.current = onResponseStateUpdate }, [onResponseStateUpdate])

  const onRawUpdateRef = useRef(onRawUpdate)
  useEffect(() => { onRawUpdateRef.current = onRawUpdate }, [onRawUpdate])

  const onConversationCreatedRef = useRef(onConversationCreated)
  useEffect(() => { onConversationCreatedRef.current = onConversationCreated }, [onConversationCreated])

  const advertiserIdRef = useRef(advertiserId)
  advertiserIdRef.current = advertiserId

  const conversationIdRef = useRef(conversationId)
  conversationIdRef.current = conversationId

  // Tracks the conversationId observed on the previous run of the
  // subscription effect. Lets us distinguish the three transitions
  // that actually matter here:
  //   • existing → different existing  — user switched conversations,
  //                                       must wipe stale buffer
  //   • existing → null                — user went back to landing,
  //                                       must wipe stale buffer
  //   • null     → existing            — a *fresh* conversation just
  //                                       got created and navigated to;
  //                                       the buffer holds the user's
  //                                       optimistic first message which
  //                                       we MUST NOT wipe (regression
  //                                       from b28db43).
  const prevConversationIdRef = useRef<number | null>(conversationId)

  useEffect(() => {
    const prevId = prevConversationIdRef.current
    prevConversationIdRef.current = conversationId

    // Reset the message buffer only on a genuine switch away from a
    // *different* previous conversation. On the null→id transition we
    // leave whatever was optimistically appended by `sendMessage`
    // intact so the just-sent first message survives the automatic
    // `navigate('/c/N')` that follows createConversation.
    const isSwitch = prevId != null && prevId !== conversationId
    if (isSwitch) {
      setChatMessages([])
      setIsChatLoading(false)
    }

    if (!conversationId) return

    // Capture the id this subscription belongs to. Every WS `received`
    // callback compares this to `conversationIdRef.current` (the
    // current URL) and bails if they differ, so late frames from a
    // channel we're about to unsubscribe from can't write to the
    // active view.
    const subscribedId = conversationId

    subscribeToConversation(conversationId, {
      onUpdate: (data: ConversationUpdateData) => {
        // Ignore updates for a conversation we've since navigated away
        // from. In-flight `received()` callbacks can still fire between
        // the user's click and the cleanup below actually calling
        // `unsubscribeFromConversation`; without this guard they'd
        // stomp on the new conversation's chatMessages / isChatLoading.
        if (conversationIdRef.current !== subscribedId) return

        onRawUpdateRef.current?.(data)
        const rs = data.response_state || (data as any).data?.response_state
        if (rs) onResponseStateUpdateRef.current?.(rs as Record<string, unknown>)

        const chatHistory = (data as any).data?.chat_history ?? (data as any).chat_history
        if (chatHistory && Array.isArray(chatHistory) && chatHistory.length > 0) {
          const relevant = chatHistory.filter((m: any) => isDisplayableMessage(m))
          const mapped: ChatMessage[] = relevant.map((m: any, i: number) => {
            let content = m.message || m.content || ''
            if (isCampaignAgentMessage(m)) content = formatCampaignAgentMessage(content)
            // `type: 'concepts'` chat turns carry a `ref.concept_ids`
            // array — hoist it onto the ChatMessage so the renderer can
            // render an inline concept-picker card grid at this turn.
            // Kept as a shallow copy (not the raw ref) to insulate the
            // UI type from BE schema drift.
            const conceptIds =
              m?.type === 'concepts' && Array.isArray(m?.ref?.concept_ids)
                ? (m.ref.concept_ids as unknown[]).filter(
                    (id): id is string => typeof id === 'string',
                  )
                : undefined
            // Sibling hoist: `type: 'social_templates'` turns carry
            // `ref.social_template_ids` for the social-media picker.
            // Same rationale as `conceptIds` — resolved against the
            // live `social_media_recommended_template.items` at render
            // time, so the id list here is enough context for the UI.
            const socialTemplateIds =
              m?.type === 'social_templates' &&
              Array.isArray(m?.ref?.social_template_ids)
                ? (m.ref.social_template_ids as unknown[]).filter(
                    (id): id is string => typeof id === 'string',
                  )
                : undefined
            return {
              id: `ws-${conversationId}-${i}`,
              type: isUserMessage(m) ? 'user' as const : 'ai' as const,
              content,
              timestamp: new Date(),
              ...(conceptIds && conceptIds.length > 0 ? { conceptIds } : {}),
              ...(socialTemplateIds && socialTemplateIds.length > 0
                ? { socialTemplateIds }
                : {}),
            }
          })
          setChatMessages(mapped)
        }

        const reply = rs?.reply_to_human ?? (rs as any)?.orchestrator?.reply_to_human
        if (reply && typeof reply === 'string') {
          setChatMessages(prev => {
            if (prev.some(m => m.content === reply && m.type === 'ai')) return prev
            return [...prev, { id: `ws-reply-${Date.now()}`, type: 'ai', content: reply, timestamp: new Date() }]
          })
        }

        // Only manage isChatLoading internally if no onRawUpdate consumer is handling it
        if (!onRawUpdateRef.current) {
          const history = (data as any).data?.chat_history ?? (data as any).chat_history
          const status = (data as any).data?.status ?? (data as any).status ?? ''
          const showThinking = shouldShowThinking(rs, history, status)
          setIsChatLoading(showThinking)
        }
      },
      onAdUnitProcessing: (d) => extraCbRef.current?.onAdUnitProcessing?.(d),
      onAdUnitCreated: (d) => extraCbRef.current?.onAdUnitCreated?.(d),
      onMediaBuyComplete: (d) => extraCbRef.current?.onMediaBuyComplete?.(d),
      onCreativeSyncCompleted: (d) => extraCbRef.current?.onCreativeSyncCompleted?.(d),
      onAiCreativeCreationCompleted: (d: AiCreativeCreationCompletedData) =>
        extraCbRef.current?.onAiCreativeCreationCompleted?.(d),
      onConnected: () => console.log('useChat: WS connected', conversationId),
      onDisconnected: () => console.log('useChat: WS disconnected'),
      onRejected: () => console.error('useChat: WS rejected'),
    })

    return () => { unsubscribeFromConversation(subscribedId) }
  }, [conversationId])

  const sendMessage = useCallback(
    async (
      message: string,
      extraPayload?: Record<string, unknown>,
      formData?: FormData,
    ) => {
      const curConvId = conversationIdRef.current
      const curAdvId = advertiserIdRef.current

      const userMsg: ChatMessage = {
        id: `user-${Date.now()}`,
        type: 'user',
        content: message,
        timestamp: new Date(),
      }
      setChatMessages(prev => [...prev, userMsg])
      setIsChatLoading(true)

      try {
        if (curConvId) {
          if (formData) {
            await conversationApi.continueConversation(curConvId, message, formData)
          } else {
            await conversationApi.continueConversation(curConvId, message, extraPayload)
          }
        } else if (curAdvId) {
          if (formData) {
            // If the caller used nested `conversation[...]` keys (multipart with
            // assets), keep the auto-injected fields under the same namespace
            // so Rails strong params accept them. Otherwise fall back to flat
            // top-level keys for backward compat with older callers.
            const usesNested = formData.has('conversation[query]') ||
              formData.has('conversation[workflow]') ||
              formData.has('conversation[status]')
            if (usesNested) {
              if (!formData.has('advertiser_id')) {
                formData.append('advertiser_id', String(curAdvId))
              }
              if (!formData.has('conversation[query]')) {
                formData.append('conversation[query]', message)
              }
            } else {
              if (!formData.has('advertiser_id')) formData.append('advertiser_id', String(curAdvId))
              if (!formData.has('query')) formData.append('query', message)
            }
            const conv = await conversationApi.createConversation(formData)
            onConversationCreatedRef.current?.(conv.id, conv as unknown as Conversation)
          } else {
            const conv = await conversationApi.createConversation({
              advertiser_id: curAdvId,
              query: message,
              ...extraPayload,
            })
            onConversationCreatedRef.current?.(conv.id, conv as unknown as Conversation)
          }
        }
      } catch (err: any) {
        const errMsg: ChatMessage = {
          id: `err-${Date.now()}`,
          type: 'ai',
          content: err?.message || 'Sorry, I encountered an error. Please try again.',
          timestamp: new Date(),
        }
        setChatMessages(prev => [...prev, errMsg])
        setIsChatLoading(false)
        throw err
      }
    },
    [],
  )

  return {
    chatMessages,
    setChatMessages,
    isChatLoading,
    setIsChatLoading,
    sendMessage,
  }
}
